<template>
    <div
        ref="rootEl"
        :class="['a-small-card-flip', className]"
        @click="onCardClick"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
        @focusin="onFocusIn"
        @focusout="onFocusOut"
        @keydown.esc="onEscape"
    >
        <div class="cards">
            <div class="card front" :inert="isCardFlipped" :aria-hidden="isCardFlipped">
                <a-small-card
                    v-bind="frontContent"
                    :theme="theme"
                    :to="null"
                />
                <div v-if="frontTrigger && isFlippable" class="trigger-row">
                    <a-button
                        ref="frontTriggerRef"
                        class="trigger"
                        disable-clamp
                        :type="null"
                        :size="null"
                        :text="frontTrigger.text"
                        :glyph="frontTrigger.glyph"
                        :event="frontFlipEvent"
                        :aria-label="frontTrigger.label || `${frontTrigger.text}: ${frontTitle}`"
                        :aria-expanded="isCardFlipped"
                        :aria-controls="backId"
                        @keydown.enter.space.capture.stop.prevent="$event.currentTarget.click()"
                        @click.stop="flipTo(true, true)"
                    />
                </div>
            </div>
            <div
                v-if="isFlippable"
                :id="backId"
                class="card back"
                :inert="!isCardFlipped"
                :aria-hidden="!isCardFlipped"
            >
                <a-button
                    v-if="hasCloseIcon"
                    ref="closeRef"
                    class="close"
                    layout="only-icon"
                    glyph="i-revoke--24"
                    :type="null"
                    :size="null"
                    :event="backFlipEvent"
                    :aria-label="back.closeLabel || `Close details: ${frontTitle}`"
                    @keydown.enter.space.capture.stop.prevent="$event.currentTarget.click()"
                    @click.stop="flipTo(false, true)"
                />
                <a-small-card
                    v-bind="backContent"
                    :to="null"
                    :theme="theme"
                />
                <div v-if="backTrigger" class="trigger-row">
                    <a-button
                        ref="backTriggerRef"
                        layout="left-icon"
                        disable-clamp
                        class="trigger trigger-back"
                        :type="null"
                        :size="null"
                        :text="backTrigger.text"
                        :glyph="backTrigger.glyph"
                        :event="backFlipEvent"
                        :aria-label="backTrigger.label || `${backTrigger.text}: ${frontTitle}`"
                        @keydown.enter.space.capture.stop.prevent="$event.currentTarget.click()"
                        @click.stop="flipTo(false, true)"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, nextTick, ref, useId } from 'vue';
import AButton from '@core/components/button/button.vue';
import ASmallCard from '@core/components/small-card/small-card.vue';
import common from '@utils/common';

defineOptions({
    name: 'ASmallCardFlip',
});

const props = defineProps({
    front: {
        type: Object,
        default: null,
    },
    back: {
        type: Object,
        default: null,
    },
    flipOn: {
        type: [String, Array],
        default: null,
        validator: (prop) => [].concat(prop).filter(Boolean).every((v) => ['clickCard', 'hover'].includes(v)),
    },
    flipDirection: {
        type: String,
        default: null,
        validator: (prop) => !prop || ['right', 'left', 'up', 'down'].includes(prop),
    },
    peekOnHover: {
        type: Boolean,
        default: true,
    },
    titleTag: {
        type: String,
        default: 'p',
    },
    theme: {
        type: String,
        validator: (prop) => ['light', 'dark'].includes(prop),
        default: 'light',
    },
});

function toCardContent(source) {
    const content = { ...(source || {}) };
    delete content.event;
    delete content.trigger;
    delete content.hasCloseIcon;
    delete content.closeLabel;
    content.titleTag = content.titleTag || props.titleTag;
    if (Array.isArray(content.labels)) {
        content.labels = content.labels.map((label) => ({ size: 'm', ...label }));
    }
    return content;
}

const frontContent = computed(() => toCardContent(props.front));
const backContent = computed(() => toCardContent(props.back));

const frontTrigger = computed(() => props.front?.trigger || null);
const backTrigger = computed(() => props.back?.trigger || null);
const hasCloseIcon = computed(() => Boolean(props.back) && props.back.hasCloseIcon !== false);
const frontFlipEvent = computed(() => buildFlipEvent('front'));
const backFlipEvent = computed(() => buildFlipEvent('back'));

function buildFlipEvent(side) {
    return {
        category: 'Interactions',
        action: 'Flip Cards',
        label: side === 'front' ? 'See more' : 'Back',
        content: common.altText(props.front?.title),
        ...props[side]?.event,
    };
}

const rootEl = ref(null);
const frontTriggerRef = ref(null);
const backTriggerRef = ref(null);
const closeRef = ref(null);
const backId = `small-card-back-${useId()}`;
const frontTitle = computed(() => common.altText(props.front?.title));
let isMovingFocus = false;
let isClickingCard = false;
const isCardFlipped = ref(false);
const isFlippable = computed(() => Boolean(props.back));

const flipModes = computed(() => [].concat(props.flipOn).filter(Boolean));
const canFlipOnCardClick = computed(() => flipModes.value.includes('clickCard'));
const canFlipOnHover = computed(() => flipModes.value.includes('hover'));

const isAnimated = computed(() => isFlippable.value && Boolean(props.flipDirection));

const className = computed(() => ({
    flippable: isFlippable.value,
    flipped: isFlippable.value && isCardFlipped.value,
    clickable: isFlippable.value && canFlipOnCardClick.value,
    animated: isAnimated.value,
    'hover-peek': props.peekOnHover,
    [`flip-${props.flipDirection}`]: isAnimated.value,
    'is-dark': props.theme === 'dark',
}));

const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, label, [role="button"], .a-button, .a-link';

function getVisibleTrigger(isFlipped) {
    return isFlipped ? (backTriggerRef.value || closeRef.value) : frontTriggerRef.value;
}

async function flipTo(isFlipped, moveFocus = false) {
    if (!isFlippable.value) return;
    isCardFlipped.value = isFlipped;
    if (!moveFocus || isClickingCard) return;
    await nextTick();
    if (isCardFlipped.value !== isFlipped) return;
    const target = getVisibleTrigger(isFlipped);
    isMovingFocus = true;
    target?.$el?.focus({ preventScroll: true });
    isMovingFocus = false;
}

function onCardClick(event) {
    if (!canFlipOnCardClick.value) return;
    if (event.target.closest(INTERACTIVE_SELECTOR)) return;
    if (!isFlippable.value) return;

    const trigger = getVisibleTrigger(isCardFlipped.value);
    if (trigger?.$el) {
        isClickingCard = true;
        try {
            trigger.$el.click();
        } finally {
            isClickingCard = false;
        }
        return;
    }

    flipTo(!isCardFlipped.value);
}

function onEscape(event) {
    if (!isCardFlipped.value) return;
    event.stopPropagation();
    flipTo(false, true);
}

function onMouseEnter() {
    if (canFlipOnHover.value) flipTo(true);
}

function onMouseLeave() {
    if (!canFlipOnHover.value) return;
    // keep the card flipped while the keyboard is still inside it
    if (rootEl.value?.contains(document.activeElement)) return;
    flipTo(false);
}

function onFocusIn() {
    if (canFlipOnHover.value && !isMovingFocus) flipTo(true);
}

function onFocusOut(event) {
    if (!canFlipOnHover.value) return;
    if (event.currentTarget.contains(event.relatedTarget)) return;
    flipTo(false);
}
</script>

<style lang="pcss" scoped>
.a-small-card-flip {
    width: 100%;
    height: 100%;
}

.cards {
    display: grid;
    width: 100%;
    height: 100%;
}

.card {
    grid-area: 1 / 1;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 24px;
    background-color: var(--av-inversed-primary);
    border: 1px solid var(--av-brand-secondary-light);
    border-radius: 16px;
    overflow: hidden;
    transition: border-color 0.15s ease-out, box-shadow 0.15s ease-out;
}

.card :deep(.a-small-card) {
    flex: 1 1 auto;
    height: auto;
    padding: 0;
    border: none;
    border-radius: 0;
    background-color: transparent;
    box-shadow: none;
    overflow: visible;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
}

.card :deep(.a-small-card) .a-picture-logo {
    max-width: 248px;
}

.back {
    border-color: var(--av-brand-primary);
    box-shadow: 0 8px 24px rgba(0, 32, 77, 0.16);
}

.back :deep(.a-small-card) {
    gap: 0;
}

.back :deep(.a-small-card) .title {
    font-size: var(--text-descriptor-size);
    line-height: var(--text-descriptor-line-height);
    font-weight: var(--text-weight-accent);
    margin-bottom: 16px;
    padding-bottom: 16px;
    padding-inline-end: 32px;
    color: var(--av-fixed-light);
    border-bottom: 1px solid var(--av-fixed-info-accent);
}

.back :deep(.a-small-card) .list {
    gap: 0;
    list-style: none;
    padding-inline-start: 0;
    color: var(--av-nav-primary);
}

.back :deep(.a-small-card) .text {
    font-size: var(--text-paragraph-size);
    line-height: var(--text-paragraph-line-height);
    color: var(--av-nav-primary);
}

.back :deep(.a-small-card) .list-item {
    font-size: var(--text-paragraph-size);
    line-height: var(--text-paragraph-line-height);
    display: block;
    position: relative;
    padding-inline-start: 16px;
}

.back :deep(.a-small-card) .list-item::before {
    content: '';
    position: absolute;
    inset-inline-start: 0;
    top: 0.7em;
    width: 3px;
    height: 3px;
    background-color: currentColor;
}

.back :deep(.a-small-card) .links {
    margin-top: 24px;
}

.back :deep(.a-small-card) .buttons {
    padding-top: 0;
    margin: 40px 0;
}

.front :deep(.a-small-card) {
    gap: 24px;
}

.front :deep(.a-small-card) .labels,
.front :deep(.a-small-card) .labels-item {
    height: 24px;
}

.front :deep(.a-small-card) .link-name {
    font-size: var(--text-caption-size);
    line-height: var(--text-caption-line-height);
    color: var(--av-nav-primary);
}

.front :deep(.a-small-card) .link-size {
    font-size: var(--text-caption-size);
    line-height: var(--text-caption-line-height);
    color: #8c95ad;
}

.front :deep(.a-small-card) .labels + .title {
    margin-top: -16px;
}

.front :deep(.a-small-card) .title {
    font-size: var(--text-title-size);
    line-height: var(--text-title-line-height);
    font-weight: var(--text-weight-accent);
    padding-inline-end: 24px;
    color: var(--av-nav-primary);
}

.front :deep(.a-small-card) .text {
    font-size: var(--text-lead-size);
    line-height: var(--text-lead-line-height);
    padding-inline-end: 24px;
    color: var(--av-nav-primary);
}

.trigger-row {
    padding-top: 24px;
    border-top: 1px solid var(--av-brand-secondary-accent);
}

.trigger.a-button {
    gap: 8px;
    border: none;
    border-radius: 0;
    padding: 0;
    display: inline-flex;
    cursor: pointer;
    background: none;
    align-items: center;
    color: var(--av-brand-primary);
    max-width: 100%;
    white-space: normal;
}

.trigger :deep(.a-button__content) {
    font-size: var(--text-paragraph-size);
    line-height: var(--text-paragraph-line-height);
    font-weight: var(--text-weight-accent);
    color: var(--av-link-primary);
    max-width: none;
}

.trigger :deep(.a-glyph) {
    fill: var(--av-link-primary);
    height: 16px;
}

.trigger:hover :deep(.a-button__content) {
    color: var(--av-link-primary-hover);
}

.trigger:hover :deep(.a-glyph) {
    fill: var(--av-link-primary-hover);
}

.close.a-button {
    position: absolute;
    top: 20px;
    inset-inline-end: 20px;
    display: flex;
    padding: 4px;
    border: none;
    background: none;
    cursor: pointer;
    color: var(--av-fixed-light);
    z-index: 1;
}

.close :deep(.a-glyph) {
    fill: var(--av-fixed-light);
    width: 24px;
    height: 24px;
}

.close:hover {
    color: var(--av-fixed-danger);
}

.close:hover :deep(.a-glyph) {
    fill: var(--av-fixed-danger);
}

.clickable {
    cursor: pointer;
}

.clickable .front {
    user-select: none;
}

.flip-right {
    --flip-rotate: rotateY(180deg);
    --flip-peek: rotateY(14deg);
}

.flip-left {
    --flip-rotate: rotateY(-180deg);
    --flip-peek: rotateY(-14deg);
}

.flip-up {
    --flip-rotate: rotateX(180deg);
    --flip-peek: rotateX(14deg);
}

.flip-down {
    --flip-rotate: rotateX(-180deg);
    --flip-peek: rotateX(-14deg);
}

.flippable {
    position: relative;
    perspective: 1600px;
}

.flippable .back {
    pointer-events: none;
}

.flippable.flipped .front {
    pointer-events: none;
}

.flippable.flipped .back {
    pointer-events: auto;
}

.flippable:not(.animated) .back {
    display: none;
}

.flippable:not(.animated).flipped .front {
    display: none;
}

.flippable:not(.animated).flipped .back {
    display: flex;
}

.flippable.animated {
    --flip-duration: 0.8s;
    --flip-easing: cubic-bezier(0.4, 0.2, 0.2, 1);
}

.flippable.animated .cards {
    transform: translateZ(0);
    transform-style: preserve-3d;
    -webkit-transform-style: preserve-3d;
    transition: transform 0.6s cubic-bezier(0.4, 0.2, 0.2, 1);
}

.flippable.animated .card {
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
}

.flippable.animated .front,
.flippable.animated .back {
    transition: transform var(--flip-duration) var(--flip-easing);
}

.flippable.animated .front {
    z-index: 1;
    transform: translateZ(0);
}

.flippable.animated .back {
    transform: var(--flip-rotate, rotateY(180deg)) translateZ(0);
}

.flippable.animated.flipped .cards {
    transform: none;
}

.flippable.animated.flipped .front {
    z-index: 0;
    transform: var(--flip-rotate, rotateY(180deg)) translateZ(0);
}

.flippable.animated.flipped .back {
    z-index: 1;
    transform: translateZ(0);
}

@media (hover: hover) {
    .flippable.animated.hover-peek:not(.flipped) .cards:has(.front .trigger:hover),
    .flippable.animated.hover-peek.clickable:not(.flipped):hover .cards {
        transform: var(--flip-peek, rotateY(-14deg));
    }

    .flippable.animated.hover-peek:not(.flipped) .cards:has(.front .trigger:hover) .front,
    .flippable.animated.hover-peek.clickable:not(.flipped):hover .cards .front {
        box-shadow: 0 8px 24px rgba(0, 32, 77, 0.12);
    }
}

@media (prefers-reduced-motion: reduce) {
    .flippable.animated .cards,
    .flippable.animated .front,
    .flippable.animated .back {
        transition: none;
    }

    .flippable.animated.hover-peek:not(.flipped) .cards:has(.front .trigger:hover),
    .flippable.animated.hover-peek.clickable:not(.flipped):hover .cards {
        transform: none;
    }
}

.is-dark .card {
    border-color: var(--av-brand-secondary-light);
    background-color: var(--av-brand-secondary-accent);
}

.is-dark :deep(.a-small-card) .title {
    color: var(--av-link-inversed);
}

.is-dark :deep(.a-small-card) .text,
.is-dark :deep(.a-small-card) .list {
    color: var(--av-inversed-primary);
}

.is-dark :deep(.a-small-card) .links-item {
    border-color: var(--av-link-inversed-light);
}

.is-dark :deep(.a-small-card) .link-item .link-name {
    color: var(--av-inversed-primary);
}

.is-dark :deep(.a-small-card) .link-item .link-size {
    color: var(--av-link-inversed-light);
}

.is-dark :deep(.a-small-card) .link-icon,
.is-dark :deep(.a-small-card) .link-download {
    fill: var(--av-link-inversed);
}

.is-dark .trigger-row {
    border-color: var(--av-link-inversed-light);
}

.is-dark .trigger :deep(.a-button__content) {
    color: var(--av-link-inversed);
}

.is-dark .trigger :deep(.a-glyph) {
    fill: var(--av-link-inversed);
}

.is-dark .close :deep(.a-glyph) {
    fill: var(--av-link-inversed);
}
</style>
